import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Loader2, ChefHat, Check, AlertTriangle, ArrowUpRight, ShieldOff, Sparkles,
} from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return null;
  }
};

const Account = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/?auth=1"); return; }
    api.get("/subscription/me")
      .then((r) => setMe(r.data))
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const refresh = () => {
    api.get("/subscription/me").then((r) => setMe(r.data)).catch(() => {});
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await api.post("/subscription/cancel");
      if (res.data?.already_canceled) {
        toast.info("Already canceled — you're just running the clock down.");
      } else {
        toast.success("Kitchen Pass canceled. Cook the last of it.");
      }
      setConfirmOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't cancel — try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }
  if (!user) return null;

  const status = me?.subscription_status || "free";
  const plan = me?.subscription_plan;
  const periodEnd = formatDate(me?.subscription_current_period_end);
  const isActive = !!me?.is_active;
  const isCanceled = status === "canceled";
  const isAdmin = !!user.is_admin;

  return (
    <div className="min-h-screen bg-stone-950 pt-24 pb-32">
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10">

        {/* Header */}
        <div className="mb-12 border-b border-stone-800 pb-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="magazine-number text-4xl">03</span>
            <div className="w-10 h-px bg-orange-600" />
            <span className="eyebrow">The Back Office</span>
          </div>
          <h1
            data-testid="account-title"
            className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-stone-100 leading-[0.95] sm:leading-[0.9] tracking-tight sm:tracking-tighter"
          >
            Your <span className="italic text-orange-600">Cinder &amp; Sorrel</span> account.
          </h1>
          <p className="mt-6 max-w-2xl text-stone-400">
            Signed in as{" "}
            <span data-testid="account-email" className="text-stone-200 font-mono-accent">{user.email}</span>.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">

          {/* Owner-only rescue: on any env where gcanth@gmail.com isn't yet admin */}
          {!isAdmin && (user.email || "").toLowerCase() === "gcanth@gmail.com" && (
            <div className="lg:col-span-3 border border-orange-800/60 bg-orange-950/25 p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="max-w-2xl">
                <div className="eyebrow text-orange-500 mb-2">Owner not yet promoted</div>
                <p className="font-serif-display text-2xl md:text-3xl text-stone-100 leading-tight">
                  This looks like the owner&apos;s account, but admin isn&apos;t on yet.
                </p>
                <p className="mt-3 text-sm text-stone-400">
                  One tap — locks admin + Owner Pass on this account. Only <span className="font-mono-accent text-stone-200">gcanth@gmail.com</span> can trigger it; anyone else gets rejected.
                </p>
              </div>
              <button
                type="button"
                data-testid="account-owner-claim"
                onClick={async () => {
                  try {
                    const res = await api.post("/admin/bootstrap", {});
                    if (res.data?.ok) {
                      toast.success("Owner access granted. Log out and back in.");
                      setTimeout(() => window.location.reload(), 1200);
                    } else {
                      toast.error("Something went sideways — check console.");
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Bootstrap failed.");
                  }
                }}
                className="inline-flex items-center gap-2 bg-orange-700 hover:bg-orange-800 text-stone-50 px-6 py-3.5 uppercase text-xs tracking-[0.24em] font-medium transition-colors whitespace-nowrap"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                Claim owner access
              </button>
            </div>
          )}

          {/* Kitchen Pass card */}
          <div className="lg:col-span-2 border border-stone-800 bg-stone-900/40 p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <ChefHat className="w-4 h-4 text-orange-500" />
              <span className="eyebrow text-orange-500">The Kitchen Pass</span>
            </div>

            {isAdmin ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-orange-600/50 bg-orange-950/40 text-orange-400 text-[10px] uppercase tracking-[0.28em]">
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                    Owner Pass
                  </span>
                </div>
                <h2 className="font-serif-display text-4xl md:text-5xl text-stone-100 leading-none tracking-tight">
                  Every door&apos;s open.
                </h2>
                <p className="mt-4 text-stone-400 max-w-xl">
                  You&apos;re the owner — unlimited AI generations, unlimited plating, the admin panel and every future feature all on the house. Perpetual, un-billable, yours.
                </p>
                <div className="mt-8 grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="border border-stone-800 p-4">
                    <div className="eyebrow mb-2">Today&apos;s tab</div>
                    <div data-testid="account-admin-badge" className="font-serif-display text-2xl text-stone-100">
                      <span className="text-lime-500">∞</span>
                      <span className="text-stone-500 text-lg"> / unlimited</span>
                    </div>
                  </div>
                  <div className="border border-stone-800 p-4">
                    <div className="eyebrow mb-2">Renews</div>
                    <div className="font-serif-display text-2xl text-stone-100">
                      Never
                    </div>
                  </div>
                </div>
              </>
            ) : isActive && !isCanceled ? (
              <>
                <h2 className="font-serif-display text-4xl md:text-5xl text-stone-100 leading-none tracking-tight">
                  Active.
                </h2>
                <p className="mt-4 text-stone-400 max-w-xl">
                  You&apos;re on the <span className="text-stone-200">{plan || "monthly"}</span> pass —
                  five bespoke recipes a day, plated properly, quota resets midnight UTC.
                </p>
                <div className="mt-8 grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="border border-stone-800 p-4">
                    <div className="eyebrow mb-2">Today&apos;s tab</div>
                    <div data-testid="account-quota" className="font-serif-display text-2xl text-stone-100">
                      <span className="text-lime-500">{me.generations_remaining}</span>
                      <span className="text-stone-500 text-lg"> / {me.generations_limit} left</span>
                    </div>
                  </div>
                  <div className="border border-stone-800 p-4">
                    <div className="eyebrow mb-2">Renews</div>
                    <div data-testid="account-renews" className="font-serif-display text-2xl text-stone-100">
                      {periodEnd || "—"}
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-stone-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="max-w-md">
                    <div className="eyebrow text-stone-500 mb-1">Bin the pass</div>
                    <p className="text-sm text-stone-400">
                      Cancel any time. You keep cooking on the pass until {periodEnd || "the end of your billing period"} — then it lapses quietly.
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="account-cancel-open"
                    onClick={() => setConfirmOpen(true)}
                    className="inline-flex items-center gap-2 border border-stone-700 hover:border-orange-600 text-stone-300 hover:text-orange-500 px-5 py-3 uppercase text-xs tracking-[0.24em] font-medium transition-colors"
                  >
                    <ShieldOff className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Cancel Kitchen Pass
                  </button>
                </div>
              </>
            ) : isCanceled ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  <span className="eyebrow text-orange-500">Canceled — running down</span>
                </div>
                <h2 className="font-serif-display text-4xl md:text-5xl text-stone-100 leading-none tracking-tight">
                  Last of the pass.
                </h2>
                <p className="mt-4 text-stone-400 max-w-xl">
                  You&apos;ve canceled the pass. Access lasts until{" "}
                  <span data-testid="account-lapses-on" className="text-stone-200">{periodEnd || "the end of your billing period"}</span>
                  , then it lapses. Nothing else to do — no charge is coming.
                </p>
                <Link
                  to="/subscribe"
                  data-testid="account-resub-cta"
                  className="mt-8 inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 text-stone-50 px-6 py-3 uppercase text-xs tracking-[0.24em] font-medium transition-colors"
                >
                  Change your mind
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <>
                <h2 className="font-serif-display text-4xl md:text-5xl text-stone-100 leading-none tracking-tight">
                  No pass yet.
                </h2>
                <p className="mt-4 text-stone-400 max-w-xl">
                  The AI kitchen at Cinder &amp; Sorrel is pass-only. Grab one and cook five bespoke plates a day, plated by Nano Banana.
                </p>
                <Link
                  to="/subscribe"
                  data-testid="account-sub-cta"
                  className="mt-8 inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 text-stone-50 px-6 py-3 uppercase text-xs tracking-[0.24em] font-medium transition-colors"
                >
                  See the plans
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>

          {/* Right column: quick links */}
          <div className="space-y-4">
            <div className="border border-stone-800 p-6">
              <div className="eyebrow mb-4">Around the kitchen</div>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link
                    to="/cookbook"
                    data-testid="account-link-cookbook"
                    className="text-stone-300 hover:text-orange-500 ink-link inline-flex items-center gap-2"
                  >
                    Your cookbook <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cookbook/manage"
                    data-testid="account-link-shares"
                    className="text-stone-300 hover:text-orange-500 ink-link inline-flex items-center gap-2"
                  >
                    Your shared cookbooks <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/submit"
                    data-testid="account-link-submit"
                    className="text-stone-300 hover:text-orange-500 ink-link inline-flex items-center gap-2"
                  >
                    Submit a recipe <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </li>
                {isAdmin && (
                  <li>
                    <Link
                      to="/admin"
                      data-testid="account-link-admin"
                      className="text-orange-500 hover:text-orange-400 ink-link inline-flex items-center gap-2"
                    >
                      Admin moderation <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            <div className="border border-stone-800 p-6">
              <div className="eyebrow mb-4">Sign out</div>
              <button
                type="button"
                data-testid="account-logout"
                onClick={() => { logout(); navigate("/"); }}
                className="w-full text-left text-sm text-stone-400 hover:text-orange-500 transition-colors"
              >
                Log out of {user.email}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm dialog */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-stone-950/85 backdrop-blur-sm flex items-center justify-center px-6"
          data-testid="account-cancel-dialog"
          onClick={() => !busy && setConfirmOpen(false)}
        >
          <div
            className="w-full max-w-lg border border-stone-800 bg-stone-950 p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <span className="eyebrow text-orange-500">Cancel the pass?</span>
            </div>
            <h3 className="font-serif-display text-3xl md:text-4xl text-stone-100 leading-tight tracking-tight">
              You&apos;ll keep cooking until <span className="italic text-orange-600">{periodEnd || "the period ends"}</span>.
            </h3>
            <ul className="mt-6 space-y-3 text-sm text-stone-400">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-lime-500 mt-0.5 shrink-0" />
                <span>Access stays on until {periodEnd || "your renewal date"} — cook it out.</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-lime-500 mt-0.5 shrink-0" />
                <span>No further charge. The pass simply lapses.</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-lime-500 mt-0.5 shrink-0" />
                <span>Your saved recipes and cookbooks stick around, obviously.</span>
              </li>
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="account-cancel-confirm"
                disabled={busy}
                onClick={cancel}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-stone-800 hover:bg-orange-800 disabled:opacity-60 text-stone-50 px-6 py-3.5 uppercase text-xs tracking-[0.24em] font-medium transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" strokeWidth={1.5} />}
                {busy ? "Canceling..." : "Yes, cancel it"}
              </button>
              <button
                type="button"
                data-testid="account-cancel-abort"
                disabled={busy}
                onClick={() => setConfirmOpen(false)}
                className="flex-1 border border-stone-700 hover:border-stone-500 text-stone-300 px-6 py-3.5 uppercase text-xs tracking-[0.24em] font-medium transition-colors"
              >
                Keep the pass
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
